import { createRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { CaseWorkspaceHeader } from "@/components/case/case-workspace-header";
import { VersionLevelTabs } from "@/components/case/version-level-tabs";
import { PhoneJsonEditorPanel } from "@/components/phone-json-editor/phone-json-editor-panel";
import {
  PhoneJsonEditorSessionProvider,
  usePhoneJsonEditorSession,
} from "@/components/phone-json-editor/phone-json-editor-session";
import { caseTitle, useCaseDetail } from "@/hooks/use-case-detail";
import { VersionLiveProvider, useVersionLive } from "@/hooks/use-version-live";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { caseLayoutRoute } from "./case-layout-route";

export const versionLayoutRoute = createRoute({
  getParentRoute: () => caseLayoutRoute,
  path: "/versions/$versionId",
  beforeLoad: ({ params, location }) => {
    const base = `/cases/${params.caseId}/versions/${params.versionId}`;
    if (location.pathname === base || location.pathname === `${base}/`) {
      // New versions land on meta for review before pipeline.
      throw redirect({
        to: "/cases/$caseId/versions/$versionId/meta",
        params: {
          caseId: params.caseId,
          versionId: params.versionId,
        },
      });
    }
  },
  component: VersionLayoutPage,
});

function VersionLayoutPage() {
  const auth = useAuth();
  const { caseId, versionId } = versionLayoutRoute.useParams();
  const detailQuery = useCaseDetail(caseId);

  if (auth.status === "loading" || detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col">
            <Skeleton className="h-3.5 w-40" />
            <div className="mt-4 flex items-center justify-between gap-4">
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-7 w-40" />
            </div>
            <div className="mt-5 flex gap-3">
              <Skeleton className="h-9 w-52" />
              <Skeleton className="h-9 w-72" />
            </div>
          </div>
          <Skeleton className="h-8 w-full max-w-lg" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>案件未找到</AlertTitle>
        <AlertDescription>
          案件未找到或无法加载。
        </AlertDescription>
      </Alert>
    );
  }

  const version = detailQuery.data.versions.find((v) => v.id === versionId);
  if (!version) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink render={<Link to="/" />}>案件</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                render={
                  <Link
                    to="/cases/$caseId/versions"
                    params={{ caseId }}
                  />
                }
              >
                {caseTitle(detailQuery.data)}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>版本未找到</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Alert variant="destructive">
          <AlertTitle>版本未找到</AlertTitle>
          <AlertDescription>
            该版本不属于此案件。请从版本表中选择其他版本。
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <VersionLiveProvider caseId={caseId} versionId={versionId}>
      <PhoneJsonEditorSessionProvider
        caseId={caseId}
        versionId={versionId}
        version={version}
      >
        <VersionLayoutChrome
          caseId={caseId}
          versionId={versionId}
          detail={detailQuery.data}
          version={version}
        />
      </PhoneJsonEditorSessionProvider>
    </VersionLiveProvider>
  );
}

function VersionLayoutChrome({
  caseId,
  versionId,
  detail,
  version,
}: {
  caseId: string;
  versionId: string;
  detail: NonNullable<ReturnType<typeof useCaseDetail>["data"]>;
  version: NonNullable<
    NonNullable<ReturnType<typeof useCaseDetail>["data"]>["versions"][number]
  >;
}) {
  const { liveStatus } = useVersionLive();
  const editor = usePhoneJsonEditorSession();
  const showEditor = editor.open || editor.dirty;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-5">
        <CaseWorkspaceHeader
          caseId={caseId}
          detail={detail}
          version={version}
          liveStatus={liveStatus}
        />
        <VersionLevelTabs caseId={caseId} versionId={versionId} />
      </div>
      <div
        className={cn(
          "flex flex-col gap-4",
          editor.open && "lg:flex-row lg:items-start",
        )}
      >
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
        {showEditor ? (
          <aside
            className={cn(
              "min-h-[32rem] overflow-hidden rounded-xl border border-border bg-background",
              editor.open
                ? "lg:sticky lg:top-4 lg:h-[calc(100svh-2rem)] lg:w-[min(44rem,48%)] lg:shrink-0"
                : "hidden",
            )}
          >
            <PhoneJsonEditorPanel />
          </aside>
        ) : null}
      </div>
    </div>
  );
}
